import { Tweet } from "@/types/serializers"
import formatDate from "@/utils/formatDate"
import clsx from "clsx"
import Button from "./Button"
import { TrashIcon } from "@heroicons/react/20/solid"
import { useForm } from "@inertiajs/react"

const AdminTweets = ({
  tweets,
  promotionSlug,
}: {
  tweets?: Tweet[]
  promotionSlug: string
}) => {
  const { delete: destroy, processing: isDeleting } = useForm()

  const handleDeleteClick = async (tweetId: number) => {
    if (!window.confirm("Are you sure you want to delete this tweet?")) {
      return
    }

    destroy(`/p/${promotionSlug}/tweets/${tweetId}`, {
      async: true,
      preserveScroll: true,
      preserveState: true,
    })
  }

  if (!tweets) return null

  return (
    <div className="mt-3 space-y-5 overflow-hidden rounded-2xl border border-gray-100 bg-white px-4 pt-5 pb-6 shadow sm:px-6">
      <h2 className="text-base font-semibold text-gray-900">
        {tweets.length} {tweets.length === 1 ? "tweet" : "tweets"} found
      </h2>

      {tweets.length > 0 ? (
        <ul role="list" className="divide-y divide-gray-100">
          {tweets.map((tweet, index) => {
            const isFirst = index === 0
            const isLast = index === tweets.length - 1
            const displayDate = tweet.posted_at ?? tweet.created_at

            return (
              <li
                key={tweet.id}
                className={clsx(
                  "flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap",
                  {
                    "pb-5": isFirst && !isLast,
                    "pt-5": isLast && !isFirst,
                    "py-5": !isFirst && !isLast,
                  }
                )}
              >
                <div className="text-sm/6 font-semibold text-gray-900">
                  {index + 1}.
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm/6 font-semibold text-gray-900">
                      <a
                        href={tweet.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="hover:underline"
                      >
                        {tweet.tweet_username}
                      </a>
                    </p>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                      {tweet.source === "x_api" ? "Auto-detected" : "Manual"}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-x-2 text-xs/5 text-gray-500">
                    <p>
                      <time dateTime={displayDate}>
                        {formatDate({ date: new Date(displayDate) })}
                      </time>
                    </p>
                    {tweet.game_id ? (
                      <>
                        <span aria-hidden="true">·</span>
                        <a
                          href={`/games/${tweet.game_id}`}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="hover:underline"
                        >
                          Game
                        </a>
                      </>
                    ) : null}
                  </div>
                </div>
                <div>
                  <Button
                    size="sm"
                    style="secondary"
                    className="text-red-500"
                    disabled={isDeleting}
                    onClick={() => handleDeleteClick(tweet.id)}
                  >
                    <TrashIcon className="size-4" />
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}

export default AdminTweets
